/** @format */
import { DrawerProps } from '@mpb-com/feather/components/molecules/drawer';
import { Context, ReactNode, createContext } from 'react';

import { ModalSize, ModalVariant } from '@toucan/client/global/application';

export type BlackoutContextProps = {
    visible: boolean;
};

export type AdditionalHeaderContextProps = {
    start?: number;
    end?: number;
};

export enum ScrollLock {
    MODAL = 'modal',
    TYPEAHEAD = 'typeahead',
    // We also have a drawer in the application context but the scroll lock is handled internally
}

export type ModalContextProps = {
    visible: boolean;
    content: ReactNode;
    size: ModalSize;
    closeLabel: string;
    isClosable: boolean;
    onClose?: () => void;
    variant?: ModalVariant;
};

export type LegalLinksContextProps = Record<
    string,
    {
        urlSlug: string;
    }
>;

export type ModalOptions = Partial<
    Pick<
        ModalContextProps,
        'size' | 'closeLabel' | 'isClosable' | 'onClose' | 'variant'
    >
>;

export type ApplicationContextProps = {
    stickyFooterHeight: number;
    setStickyFooterHeight: (arg: number) => void;
    userAgentDetectedAsBot: boolean;
    blackout: BlackoutContextProps;
    modal: ModalContextProps;
    drawer: DrawerProps;
    additionalHeaderDisplayBoundary: AdditionalHeaderContextProps;
    legalLinks: LegalLinksContextProps;
    setBlackoutVisible: (arg: boolean) => void;
    setModalVisible: (arg: boolean, options?: ModalOptions) => void;
    setModalContent: (arg: ReactNode) => void;
    setModalSize: (arg: ModalSize) => void;
    setModalCloseLabel: (label: string) => void;
    openDrawer: (drawer: Omit<DrawerProps, 'open'>) => void;
    closeDrawer: () => void;
    addPageScrollLock: (lock: ScrollLock, selectors?: string[]) => void;
    removePageScrollLock: (lock: ScrollLock, selectors?: string[]) => void;
    setAdditionalHeaderDisplayBoundary: ({
        start,
        end,
    }: AdditionalHeaderContextProps) => void;
    isIntercomLauncherVisible: boolean;
    setIsIntercomLauncherVisible: (arg: boolean) => void;
};

export const ApplicationContext: Context<ApplicationContextProps> =
    createContext({} as ApplicationContextProps);

ApplicationContext.displayName = 'ApplicationContext';
